import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { AboutDialog } from "./AboutDialog"
import { 
  FolderOpen, 
  X
} from "lucide-react"
import { 
  detectVideoFormat,
  getSupportedVideoFormatsForInput,
  isFormatSupportedByBrowser
} from "@/lib/utils"

interface ToolbarProps {
  onOpenVideo: (file: File) => void
  onOpenVideoUrl?: (video: { id: string; name: string; blobUrl: string; url?: string }) => void
  onQuit: () => void
  disabled?: boolean
}

export function Toolbar({
  onOpenVideo,
  onOpenVideoUrl: _onOpenVideoUrl,
  onQuit,
  disabled: _disabled = false
}: ToolbarProps) {
  // Get supported video formats for the file input
  const supportedFormats = getSupportedVideoFormatsForInput();

  // Handle file input for video
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Detect video format
    const format = detectVideoFormat(file);
    
    if (!format) {
      console.warn("Unknown video format detected:", file.name, "This file may not be supported.");
      return;
    }
    
    // Check if the format is supported by the browser
    const isSupported = isFormatSupportedByBrowser(format);
    
    if (!isSupported) {
      console.warn(`Video format ${format.format} is not supported by your browser. Try converting to MP4, WebM, or Ogg format.`);
      return;
    }
    
    // Format is supported, proceed with loading
    onOpenVideo(file);
  };
  
  // Handle file input click
  const handleOpenVideo = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = supportedFormats;
    
    // Add an on change handler
    input.onchange = (e) => handleFileChange(e as unknown as React.ChangeEvent<HTMLInputElement>);
    
    // Trigger the file dialog
    input.click();
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 min-h-14 px-4 py-2 border-b">
      <div className="flex items-center gap-2">
        <Button 
          variant="outline" 
          onClick={handleOpenVideo}
          className="flex items-center gap-2"
          size="sm"
        >
          <FolderOpen className="h-4 w-4" />
          <span className="hidden sm:inline">Open Video</span>
          <span className="sm:hidden">Open</span>
        </Button>
        <Badge variant="secondary" className="text-xs whitespace-nowrap">
          <span className="hidden sm:inline">Supported: </span>MP4, WebM, Ogg
        </Badge>
      </div>
      
      <div className="flex items-center gap-2">
        <AboutDialog version="1.0.0" />
        
        <Button 
          variant="outline" 
          onClick={onQuit}
          size="icon"
          title="Quit"
          className="h-8 w-8"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

    </div>
  )
}